import React, { FC, useState } from "react";
import clsx from "clsx";
import styles from "../../../hygienist/ProfilePage/components/components.module.scss";
import localStyles from "./components.module.scss";
import RadioButton from 'components/RadioButton/RadioButton';
import CloseIcon from '@material-ui/icons/Close';

interface ChooseBioProps {
    isOpen: boolean;
    spellCheckedResponse: string;
    enhancedResponse: string;
    onBack: () => void;
    onSave: (about: string, saveAsBase?: boolean) => void;
    setRemoveContactInfoModal: (removeContactInfoModal: boolean) => void;
    removeContactInfoModal: boolean;
    rewritesLeft: number;
    isCoverLetter?: boolean;
    isJobApplication?: boolean;
}

export const ChooseBio: FC<ChooseBioProps> = ({
    isOpen,
    spellCheckedResponse,
    enhancedResponse,
    onBack,
    onSave,
    rewritesLeft,
    isCoverLetter,
    isJobApplication = false
}) => {

    const responses = {
        spellChecked: spellCheckedResponse,
        enhanced: enhancedResponse
    };

    const [selected, setSelected] = useState<string | null>(null);
    /**
     * T2-5836: the "Use this as my new Base Cover Letter" checkbox must be
     * an *explicit* per-selection opt-in. Whenever the user switches which
     * option is active (including re-selecting the same option or coming
     * back to one they previously ticked) the checkbox resets to false —
     * they have to tick it again for the currently-active option. This
     * guarantees the saved intent matches the option visible at submit
     * time and nothing lingers from a previous pick.
     */
    const [saveAsBase, setSaveAsBase] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const handleClick = (selectedItem: string) => {
        // Reset the save-as-base checkbox on every option switch (T2-5836).
        setSaveAsBase(false);
        if (selected === selectedItem) {
            setSelected(null);
        } else {
            setSelected(selectedItem);
        }
        setSubmitError(null);
    };

    const handleSaveAsBaseClick = () => {
        setSaveAsBase((prev) => !prev);
    };

    const handleSubmit = () => {
        if (!selected) {
            setSubmitError('Please select your preferred option to continue');
            return;
        }
        onSave(
            selected === "0" ? responses.spellChecked : responses.enhanced,
            isJobApplication ? saveAsBase : undefined,
        );
        onBack();
    };

    if (!isOpen) return null;

    return (
        <div className={styles.wizardModalOverlay} onClick={onBack}>
            <div className={styles.wizardModalContainer} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className={styles.wizardModalHeader}>
                    <div className={styles.wizardHeaderContent}>
                        <h1 className={styles.wizardModalTitle}>
                            {isCoverLetter ? "Cover Letter" : "About You"}
                        </h1>
                        <p className={styles.wizardModalSubtitle}>
                            Select the version you prefer to continue
                        </p>
                    </div>
                    <button className={styles.wizardCloseButton} onClick={onBack} aria-label="Close modal">
                        <CloseIcon />
                    </button>
                </div>

                {/* Body */}
                <div className={styles.wizardModalBody}>
                    <div className={styles.wizardInfoBox}>
                        <h4>We've got two options!</h4>
                        <p>Select the one you prefer to continue. Not loving either? <a className={localStyles.backLink} onClick={onBack}>Go back and edit your original</a></p>
                    </div>

                    {/* Minimal Version Option */}
                    <div
                        className={clsx(styles.wizardRadioOption, { [styles.selected]: selected === "0" })}
                        onClick={() => handleClick("0")}
                    >
                        <RadioButton
                            isActive={selected === "0"}
                            handleClick={() => handleClick("0")}
                            item={"0"}
                        />
                        <div className={styles.radioContent}>
                            <div className={styles.radioLabel}>Minimal Version</div>
                            <div className={styles.radioDescription}>
                                {isCoverLetter ? (
                                    <div style={{ whiteSpace: 'pre-wrap' }}>
                                        {spellCheckedResponse}
                                    </div>
                                ) : (
                                    <p>{spellCheckedResponse}</p>
                                )}
                            </div>
                            {isCoverLetter && isJobApplication && selected === "0" && (
                                <label
                                    style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <input
                                        type="checkbox"
                                        checked={saveAsBase}
                                        onChange={handleSaveAsBaseClick}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <span>Use this as my new Base Cover Letter</span>
                                </label>
                            )}
                        </div>
                    </div>

                    {/* Extra Flair Option */}
                    <div
                        className={clsx(styles.wizardRadioOption, { [styles.selected]: selected === "1" })}
                        onClick={() => handleClick("1")}
                    >
                        <RadioButton
                            isActive={selected === "1"}
                            handleClick={() => handleClick("1")}
                            item={"1"}
                        />
                        <div className={styles.radioContent}>
                            <div className={styles.radioLabel}>Extra Flair</div>
                            <div className={styles.radioDescription}>
                                {isCoverLetter ? (
                                    <div style={{ whiteSpace: 'pre-wrap' }}>
                                        {enhancedResponse}
                                    </div>
                                ) : (
                                    <p>{enhancedResponse}</p>
                                )}
                            </div>
                            {isCoverLetter && isJobApplication && selected === "1" && (
                                <label
                                    style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <input
                                        type="checkbox"
                                        checked={saveAsBase}
                                        onChange={handleSaveAsBaseClick}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <span>Use this as my new Base Cover Letter</span>
                                </label>
                            )}
                        </div>
                    </div>

                    {!isJobApplication && (
                        <p style={{ fontSize: '13px', color: '#6B7280', textAlign: 'right', marginTop: '8px' }}>
                            Remaining re-writes: {rewritesLeft}/5
                        </p>
                    )}

                    {submitError && (
                        <div className={styles.wizardErrorMessage}>
                            {submitError}
                        </div>
                    )}

                    <p style={{ fontSize: '12px', color: '#6B7280', textAlign: 'center', marginTop: '16px' }}>
                        This is a new feature we're trying - please{' '}
                        <a
                            href="mailto:feature-feedback@tempstars.com"
                            className="external externalLink"
                            target="_system"
                            style={{ color: '#0B4598' }}
                        >
                            report any problems or feedback here
                        </a>{' '}
                        so we can make any necessary improvements.
                    </p>
                </div>

                {/* Footer */}
                <div className={styles.wizardModalFooter}>
                    <button
                        type="button"
                        className={styles.wizardCancelButton}
                        onClick={onBack}
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        className={styles.wizardSaveButton}
                        onClick={handleSubmit}
                    >
                        Continue
                    </button>
                </div>
            </div>
        </div>
    );
}
